<script setup lang="ts">
import { ref, onMounted } from 'vue';

const DIALOG_STORAGE_KEY = 'thankYouDialogClosed';

const isVisible = ref(false);

onMounted(() => {
  const isDialogClosed = localStorage.getItem(DIALOG_STORAGE_KEY);
  if (!isDialogClosed) {
    isVisible.value = true;
  }
});

const closeDialog = () => {
  isVisible.value = false;
  localStorage.setItem(DIALOG_STORAGE_KEY, 'true');
};
</script>

<template>
  <div
    v-if="isVisible"
    class="thank-you-dialog"
    role="dialog"
    aria-modal="true"
    aria-labelledby="dialog-title"
  >
    <div
      class="thank-you-dialog__overlay"
      @click="closeDialog"
    ></div>
    <div class="thank-you-dialog__content">
      <div class="thank-you-dialog__header">
        <h2
          id="dialog-title"
          class="thank-you-dialog__title"
        >
          Cześć!
        </h2>
        <button
          class="thank-you-dialog__close-button"
          aria-label="Zamknij okno"
          @click="closeDialog"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>
      <div class="thank-you-dialog__body">
        <p class="thank-you-dialog__message">
          Dziękujemy za skorzystanie z naszych usług.
          <br />
          Bylibyśmy wdzięczni za zaobserwowanie nas na
          <a
            class="thank-you-dialog__link"
            href="https://www.instagram.com/wspomnienia_z_wesela/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Instagramie
            <i class="pi pi-instagram thank-you-dialog__icon thank-you-dialog__icon--instagram"></i>
          </a>
          oraz polubienie naszego fanpage'u na
          <a
            class="thank-you-dialog__link"
            href="https://www.facebook.com/WspomnieniazWesela"
            target="_blank"
            rel="noopener noreferrer"
          >
            Facebooku
            <i class="pi pi-facebook thank-you-dialog__icon thank-you-dialog__icon--facebook"></i>
          </a>
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.thank-you-dialog {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  &__overlay {
    position: absolute;
    inset: 0;
    background-color: rgba($black, 0.5);
    backdrop-filter: blur(4px);
    animation: fade-in 0.3s ease-out;
  }

  &__content {
    position: relative;
    width: 90%;
    max-width: 60rem;
    background-color: $white-200;
    border-radius: 1.6rem;
    box-shadow: 0 8px 32px rgba($black, 0.2);
    animation: slide-up 0.3s ease-out;
    overflow: hidden;
  }

  &__header {
    padding: 2.5rem 3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 2px solid $brown-300;
    background-color: $white-200;
  }

  &__title {
    font-family: Mate, serif;
    font-size: 4rem;
    color: $brown-200;
    margin: 0;
    font-weight: 700;
    letter-spacing: -0.5px;
  }

  &__close-button {
    background: none;
    border: none;
    color: $brown-200;
    cursor: pointer;
    padding: 0.75rem;
    font-size: 2.4rem;
    transition: all 0.2s ease;
    border-radius: 50%;
    width: 3.2rem;
    height: 3.2rem;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: $brown-100;
      background-color: $brown-600;
      transform: rotate(90deg);
    }

    &:focus {
      outline: 2px solid $brown-200;
      outline-offset: 2px;
    }

    i {
      font-size: 2.4rem;
    }
  }

  &__body {
    padding: 3rem;
  }

  &__message {
    font-family: Mate, serif;
    font-size: 2.4rem;
    color: $brown-200;
    line-height: 1.6;
    text-align: center;
    margin: 0;
  }

  &__link {
    color: $brown-200;
    text-decoration: none;
    position: relative;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 700;
    font-size: 2rem;

    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 100%;
      height: 2px;
      background-color: $brown-200;
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.3s ease;
    }
  }

  &__icon {
    font-size: 2.4rem;
    transition: transform 0.3s ease;
    color: $brown-200;

    &--instagram {
      color: #e1306c;
    }

    &--facebook {
      color: #1877f2;
    }
  }

  &__link:hover {
    color: $brown-100;

    &::after {
      transform: scaleX(1);
    }

    .thank-you-dialog__icon {
      transform: scale(1.1);
    }
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slide-up {
  from {
    transform: translateY(20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@media (max-width: $media-max-phone) {
  .thank-you-dialog {
    &__content {
      width: 95%;
      max-width: 40rem;
      height: auto;
      max-height: 90vh;
      border-radius: 1.2rem;
      margin: 0.2rem auto;
      display: flex;
      flex-direction: column;
      position: relative;
      top: 0;
      transform: none;
    }

    &__header {
      padding: 1.2rem;
      position: relative;
      background-color: $white-200;
      border-bottom: 1px solid $brown-300;
    }

    &__title {
      font-size: 2.4rem;
    }

    &__body {
      padding: 1.2rem;
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__message {
      font-size: 1.6rem;
      line-height: 1.4;
      padding: 0 0.5rem;
    }

    &__link {
      font-size: 1.6rem;
      padding: 0.4rem 0;
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
    }

    &__icon {
      font-size: 1.6rem;
    }

    &__close-button {
      width: 2.8rem;
      height: 2.8rem;
      font-size: 2rem;
    }
  }
}

@media (max-width: 360px) {
  .thank-you-dialog {
    &__content {
      width: 95%;
      margin: 0.5rem auto;
    }

    &__title {
      font-size: 2.2rem;
    }

    &__message {
      font-size: 1.4rem;
    }

    &__link {
      font-size: 1.4rem;
    }

    &__icon {
      font-size: 1.4rem;
    }
  }
}
</style>
